import React, { useEffect, useState } from "react";
import {
  getOrdersByYear,
  getordertrackingfilter,
  cancelOrder,
} from "../services/Cart";
import { Dropdown, Modal, message, Tag, Divider } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import { useNavigate, useNavigation } from "react-router-dom";
import LoadingSpinner from "./LoadingSpinner";

const Orders = () => {
  const [orders, setOrders] = useState([]);
  const [years, setYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState("");
  const [loading, setLoading] = useState(true);
  const [cancelModalVisible, setCancelModalVisible] = useState(false);
  const [currentOrderToCancel, setCurrentOrderToCancel] = useState(null);
  const navigate = useNavigate();

  // Track order modal states
  const [trackOrderModalVisible, setTrackOrderModalVisible] = useState(false);
  const [currentTrackingOrder, setCurrentTrackingOrder] = useState(null);

  // Debug states
  const [apiError, setApiError] = useState("");
  const [userId, setUserId] = useState("");

  useEffect(() => {
    const user = localStorage.getItem("userId");
    setUserId(user);
    const currentYear = new Date().getFullYear().toString();

    console.log("User ID from localStorage:", user);
    console.log("Current year:", currentYear);

    if (user) {
      getOrdersYears(user, currentYear);
      getOrders(currentYear, user);
    } else {
      setLoading(false);
      message.error("Please login to view orders");
    }
  }, []);

  useEffect(() => {
    if (userId && selectedYear) {
      console.log("Fetching orders for year:", selectedYear);
      getOrders(selectedYear, userId);
    }
  }, [selectedYear, userId]);

  const getOrdersYears = async (userId, year) => {
    try {
      console.log("Fetching years for user:", userId, "year:", year);
      const data = await getOrdersByYear(userId, year);
      console.log("Years API response:", data);

      if (data && Array.isArray(data)) {
        setYears(data);
      } else {
        setYears([]);
        console.error("Invalid years data format:", data);
      }
    } catch (error) {
      console.error("Error fetching years:", error);
      setYears([]);
      setApiError("Failed to load years");
    }
  };

  const getOrders = async (userId, year) => {
    setLoading(true);
    setApiError("");
    try {
      console.log("Fetching orders - Year:", year, "UserID:", userId);
      const data = await getordertrackingfilter(userId, year);
      console.log("Orders API response:", data);

      if (data && data.data && Array.isArray(data.data)) {
        setOrders(data.data);
      } else if (data && Array.isArray(data)) {
        setOrders(data);
      } else {
        console.log("No orders data found or invalid format:", data);
        setOrders([]);
      }
    } catch (error) {
      console.error("Error fetching orders:", error);
      setOrders([]);
      setApiError("Failed to load orders");
      message.error("Failed to load orders");
    } finally {
      setLoading(false);
    }
  };

  const openTrackOrderModal = (order) => {
    setCurrentTrackingOrder(order);
    setTrackOrderModalVisible(true);
  };

  const closeTrackOrderModal = () => {
    setTrackOrderModalVisible(false);
    setCurrentTrackingOrder(null);
  };

  const openCancelConfirmation = (order) => {
    setCurrentOrderToCancel(order);
    setCancelModalVisible(true);
  };

  const handleCancelOrder = async () => {
    if (!currentOrderToCancel) return;

    try {
      const payload = {
        orderid: currentOrderToCancel?.orderid,
        desc: currentOrderToCancel?.cancellationdesc || " customer request                                                                                                                                                                                         ",
      };

      const response = await cancelOrder(payload);

      if (response.status === 200) {
        message.success("Order cancelled successfully!");



        // Update the order in state
        setOrders((prevOrders) =>
          prevOrders.map((order) =>
            order.orderid === currentOrderToCancel.orderid
              ? {
                ...order,
                orderstatus: "Cancel",

                refundltxnnumbre: response.data?.refundltxnnumbre,
                refundamount: response.data?.refundamount,
                refundstatus: response.data?.refundstatus,
                cancelorderdesc: response.data?.cancelorderdesc,
              }
              : order,
          ),
        );

        setCancelModalVisible(false);
        setCurrentOrderToCancel(null);
      } else {
        message.error("Failed to cancel order");
      }
    } catch (error) {
      console.error("Cancel order error:", error);
      message.error("Error cancelling order");
    }
  };
  // Format date helper
  const formatDate = (dateString) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      return date.toISOString().split("T")[0];
    } catch {
      return dateString;
    }
  };

  // Get status color
  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "success":
        return "green";
      case "pending":
        return "orange";
      case "cancel":
        return "red";
      case "delivered":
        return "blue";
      default:
        return "default";
    }
  };

  // Check if order is cancelled
  const isOrderCancelled = (order) => {
    return order.orderstatus?.toLowerCase() === "cancel";
  };

  // Check if order has refund details
  const hasRefundDetails = (order) => {
    return (
      order.refundltxnnumbre ||
      order.refundamount ||
      order.refundstatus ||
      order.refundltxnnumbre !== null ||
      order.refundamount !== null ||
      order.refundstatus !== null
    );
  };

  // Render refund details for cancelled orders
  const renderRefundDetails = (order) => {
    // Only show if order is cancelled AND has refund details
    if (!isOrderCancelled(order) || !hasRefundDetails(order)) return null;

    return (
      <div
        style={{
          backgroundColor: "#fff9f9 !important",
          border: "1px solid #ffccc7",
          borderRadius: "6px",
          padding: "15px",
          marginBottom: "20px",
        }}
      >
        <h4
          style={{
            fontSize: "14px",
            fontWeight: "600",
            color: "#cf1322",
            marginBottom: "12px",
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          <span>🔄 Refund Details</span>
        </h4>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
            gap: "12px",
            fontSize: "13px",
          }}
        >
          {/* Refund Amount */}
          {(order.refundamount || order.refundamount === 0) && (
            <div>
              <span
                style={{ color: "#666", display: "block", marginBottom: "4px" }}
              >
                Refund Amount:
              </span>
              <span
                style={{
                  fontWeight: "600",
                  color: "#1a1a1a",
                  fontSize: "14px",
                }}
              >
                ₹{Number(order.refundamount).toFixed(2) / 100}
              </span>
            </div>
          )}

          {/* Refund Status */}
          {order.refundstatus && (
            <div>
              <span
                style={{ color: "#666", display: "block", marginBottom: "4px" }}
              >
                Refund Status:
              </span>
              <Tag
                color={
                  order.refundstatus?.toLowerCase() === "completed" ||
                    order.refundstatus?.toLowerCase() === "success"
                    ? "green"
                    : order.refundstatus?.toLowerCase() === "pending"
                      ? "orange"
                      : order.refundstatus?.toLowerCase() === "failed"
                        ? "red"
                        : "default"
                }
                style={{
                  fontSize: "11px",
                  padding: "2px 8px",
                  borderRadius: "10px",
                }}
              >
                {order.refundstatus}
              </Tag>
            </div>
          )}

          {/* Transaction ID */}
          {order.refundltxnnumbre && (
            <div>
              <span
                style={{ color: "#666", display: "block", marginBottom: "4px" }}
              >
                Refund Transaction ID:
              </span>
              <span
                style={{
                  fontWeight: "500",
                  color: "#1a1a1a",
                  wordBreak: "break-all",
                  fontSize: "12px",
                }}
              >
                {order.refundltxnnumbre}
              </span>
            </div>
          )}

          {/* Original Transaction
          {order.tnxnumber && (
            <div>
              <span
                style={{ color: "#666", display: "block", marginBottom: "4px" }}
              >
                Original Transaction:
              </span>
              <span
                style={{
                  fontWeight: "500",
                  color: "#666",
                  wordBreak: "break-all",
                  fontSize: "12px",
                }}
              >
                {order.tnxnumber}
              </span>
            </div>
          )} */}

          {/* Original Order ID */}
          {order.tnxorder && (
            <div>
              <span
                style={{ color: "#666", display: "block", marginBottom: "4px" }}
              >
                Order ID:
              </span>
              <span
                style={{
                  fontWeight: "500",
                  color: "#666",
                  fontSize: "12px",
                }}
              >
                {order.tnxorder}
              </span>
            </div>
          )}
          {order.tnxorder && (
            <div>
              <span
                style={{ color: "#666", display: "block", marginBottom: "4px" }}
              >
                Reason:
              </span>
              <span
                style={{
                  fontWeight: "500",
                  color: "#666",
                  fontSize: "12px",
                }}
              >
                {order.cancelorderdesc}
              </span>
            </div>
          )}
        </div>

        {/* Additional refund information */}
        {order.refundstatus?.toLowerCase() === "pending" && (
          <p
            style={{
              fontSize: "12px",
              color: "#666",
              marginTop: "10px",
              fontStyle: "italic",
              backgroundColor: "#fff2f0",
              padding: "8px",
              borderRadius: "4px",
            }}
          >
            ⏳ Refund will be processed within 5-7 business days
          </p>
        )}

        {order.refundstatus?.toLowerCase() === "completed" ||
          (order.refundstatus?.toLowerCase() === "success" && (
            <p
              style={{
                fontSize: "12px",
                color: "#52c41a",
                marginTop: "10px",
                fontWeight: "500",
                backgroundColor: "#f6ffed",
                padding: "8px",
                borderRadius: "4px",
                display: "flex",
                alignItems: "center",
                gap: "6px",
              }}
            >
              ✓ Refund completed successfully
            </p>
          ))}
      </div>
    );
  };

  const yearItems = years.map((y) => ({
    key: String(y.Year || y),
    label: String(y.Year || y),
  }));

  return (
    <div
      style={{
        minHeight: "100vh",
        backgroundColor: "#f5f5f5",
        padding: "20px",
        fontFamily: "Arial, sans-serif",
      }}
    >
      {/* Main Container */}
      <div
        style={{
          maxWidth: "800px",
          margin: "0 auto",
        }}
      >
        {/* Header Section */}
        <div
          style={{
            backgroundColor: "white",
            borderRadius: "8px",
            padding: "25px 30px",
            marginBottom: "20px",
            boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          }}
        >
          <h1
            style={{
              fontSize: "28px",
              fontWeight: "600",
              color: "#1a1a1a",
              marginBottom: "8px",
            }}
          >
            Orders
          </h1>

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "15px",
            }}
          >
            <div>
              <span
                style={{
                  color: "#666",
                  fontSize: "14px",
                  fontWeight: "500",
                }}
              >
                Select Year
              </span>
            </div>
            <Dropdown
              menu={{
                items: yearItems,
                onClick: ({ key }) => {
                  console.log("Selected year:", key);
                  setSelectedYear(key);
                },
              }}
              trigger={["click"]}
            >
              <div
                style={{
                  border: "1px solid #d9d9d9",
                  borderRadius: "4px",
                  padding: "5px 12px",
                  backgroundColor: "white",
                  color: "#1a1a1a",
                  cursor: "pointer",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "8px",
                  fontSize: "14px",
                  minWidth: "120px",
                }}
              >
                <span>{selectedYear || "Select Year"}</span>
                <span style={{ fontSize: "10px" }}>▼</span>
              </div>
            </Dropdown>
          </div>

          <p
            style={{
              color: "#666",
              fontSize: "14px",
              lineHeight: "1.5",
            }}
          >
            Track your open orders & view the summary of your past orders
          </p>

          <Divider style={{ margin: "20px 0" }} />

          {/* Debug Info */}
          {apiError && (
            <div
              style={{
                backgroundColor: "#fff2f0",
                border: "1px solid #ffccc7",
                padding: "12px 16px",
                borderRadius: "6px",
                marginBottom: "20px",
                color: "#cf1322",
                fontSize: "14px",
              }}
            >
              <strong>API Error:</strong> {apiError}
            </div>
          )}
        </div>

        {/* Orders List */}
        {loading ? (
          <div
            style={{
              textAlign: "center",
              padding: "60px 20px",
              backgroundColor: "white",
              borderRadius: "8px",
              boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
            }}
          >
            <LoadingSpinner text="Loading orders..." />
          </div>
        ) : orders.length === 0 ? (
          <div
            style={{
              textAlign: "center",
              padding: "60px 20px",
              backgroundColor: "white",
              borderRadius: "8px",
              boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
            }}
          >
            <h3 style={{ color: "#666", marginBottom: "10px" }}>
              No Orders Found
            </h3>
            <p style={{ color: "#999", fontSize: "14px" }}>
              No orders found for the selected year
            </p>
          </div>
        ) : (
          <div
            style={{ display: "flex", flexDirection: "column", gap: "20px" }}
          >
            {orders.map((order, index) => (
              <div
                key={order.orderid || index}
                style={{
                  backgroundColor: "white",
                  borderRadius: "8px",
                  padding: "25px 30px",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                  cursor: isOrderCancelled(order) ? "default" : "pointer",
                  opacity: isOrderCancelled(order) ? 0.9 : 1,
                }}
                onClick={() => {
                  if (!isOrderCancelled(order)) {
                    navigate(`/order-details/${order.orderid}`, {
                      state: { order },
                    });
                  }
                }}
              >
                {/* Order Header */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    marginBottom: "20px",
                  }}
                >
                  <div>
                    <h3
                      style={{
                        margin: "0 0 8px 0",
                        fontSize: "16px",
                        fontWeight: "600",
                        color: "#1a1a1a",
                      }}
                    >
                      Order ID: {order.orderid || `Order #${index + 1}`}
                    </h3>

                    <div
                      style={{
                        color: "#666",
                        fontSize: "14px",
                        marginBottom: "4px",
                      }}
                    >
                      <strong>Total items in this order:</strong>{" "}
                      {order?.details?.length || 0}
                    </div>
                  </div>

                  <Tag
                    color={getStatusColor(order.orderstatus)}
                    style={{
                      fontSize: "12px",
                      padding: "4px 12px",
                      borderRadius: "12px",
                      fontWeight: "500",
                    }}
                  >
                    {order.orderstatus || "Pending"}
                  </Tag>
                </div>

                {/* Order Details */}
                <div style={{ marginBottom: "20px" }}>
                  <div style={{ marginBottom: "8px", fontSize: "14px" }}>
                    <span style={{ color: "#666" }}>Order value:</span>
                    <span
                      style={{
                        fontWeight: "600",
                        marginLeft: "5px",
                        color: "#1a1a1a",
                      }}
                    >
                      ₹ {Number(order.netamount || 0).toFixed(2)}
                    </span>
                  </div>

                  <div style={{ fontSize: "14px" }}>
                    <span style={{ color: "#666" }}>Order Date:</span>
                    <span
                      style={{
                        fontWeight: "500",
                        marginLeft: "5px",
                        color: "#1a1a1a",
                      }}
                    >
                      {formatDate(order.orderdate)}
                    </span>
                  </div>
                   {order.transport && (
                    <div style={{ fontSize: "14px" }}>
                      <span style={{ color: "#666" }}>Transport:</span>
                      <span
                        style={{
                          fontWeight: "500",
                          marginLeft: "5px",
                        color: "#1a1a1a",
                      }}
                    >
                      {order.transport}
                    </span>
                  </div>)}
                </div>

                {/* Show refund details if order is cancelled and has refund data */}
                {renderRefundDetails(order)}

                <Divider style={{ margin: "20px 0" }} />

                {/* Action Buttons */}
                <div
                  style={{
                    display: "flex",
                    gap: "15px",
                    flexWrap: "wrap",
                  }}
                >
                  {/* Show Track Order button only if NOT cancelled */}
                  {!isOrderCancelled(order) && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        openTrackOrderModal(order);
                      }}
                      style={{
                        padding: "10px 25px",
                        backgroundColor: "#005487",
                        color: "white",
                        border: "none",
                        borderRadius: "4px",
                        cursor: "pointer",
                        fontSize: "14px",
                        fontWeight: "500",
                        minWidth: "120px",
                      }}
                    >
                      Track Order
                    </button>
                  )}

                  {/* Show Cancel button only if order is Success (not cancelled/delivered) */}
                  {order.orderstatus === "Success" && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        openCancelConfirmation(order);
                      }}
                      style={{
                        padding: "10px 25px",
                        backgroundColor: "#dc3545",
                        color: "white",
                        border: "none",
                        borderRadius: "4px",
                        cursor: "pointer",
                        fontSize: "14px",
                        fontWeight: "500",
                        minWidth: "120px",
                      }}
                    >
                      Cancel Order
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Track Order Modal */}
      <Modal
        open={trackOrderModalVisible}
        onCancel={closeTrackOrderModal}
        footer={null}
        width={500}
        centered
        closeIcon={<CloseOutlined />}
        bodyStyle={{ padding: "0" }}
      >
        {currentTrackingOrder && (
          <div>
            {/* Modal Header */}
            <div
              style={{
                padding: "25px 30px 20px",
                borderBottom: "1px solid #f0f0f0",
              }}
            >
              <h3
                style={{
                  fontSize: "18px",
                  fontWeight: "600",
                  color: "#1a1a1a",
                  margin: 0,
                }}
              >
                Track your Order
              </h3>
            </div>

            {/* Order Info */}
            <div
              style={{
                padding: "25px 30px",
              }}
            >
              <div
                style={{
                  marginBottom: "15px",
                  fontSize: "14px",
                }}
              >
                <span style={{ color: "#666" }}>Order ID:</span>
                <span
                  style={{
                    fontWeight: "600",
                    marginLeft: "5px",
                    color: "#1a1a1a",
                  }}
                >
                  {currentTrackingOrder.orderid}
                </span>
              </div>

              <div
                style={{
                  marginBottom: "15px",
                  fontSize: "14px",
                }}
              >
                <span style={{ color: "#666" }}>
                  Total items in this order:
                </span>
                <span
                  style={{
                    fontWeight: "500",
                    marginLeft: "5px",
                    color: "#1a1a1a",
                  }}
                >
                  {currentTrackingOrder.details?.length || 0}
                </span>
              </div>

              <div
                style={{
                  marginBottom: "15px",
                  fontSize: "14px",
                }}
              >
                <span style={{ color: "#666" }}>Order value:</span>
                <span
                  style={{
                    fontWeight: "600",
                    marginLeft: "5px",
                    color: "#1a1a1a",
                  }}
                >
                  ₹ {Number(currentTrackingOrder.netamount || 0).toFixed(2)} (Including GST)
                </span>
              </div>

              <div
                style={{
                  marginBottom: "25px",
                  fontSize: "14px",
                }}
              >
                <span style={{ color: "#666" }}>Order Date:</span>
                <span
                  style={{
                    fontWeight: "500",
                    marginLeft: "5px",
                    color: "#1a1a1a",
                  }}
                >
                  {formatDate(currentTrackingOrder.orderdate)}
                </span>
              </div>

              <Divider style={{ margin: "25px 0" }} />

              {/* Tracking Steps */}
              <h4
                style={{
                  fontSize: "16px",
                  fontWeight: "600",
                  color: "#1a1a1a",
                  marginBottom: "20px",
                }}
              >
                Track your Order
              </h4>

              <div style={{ marginLeft: "10px" }}>
                {/* Step 1: Order Confirmed - Always true if order exists */}
                <div
                  style={{
                    marginBottom: "20px",
                    paddingLeft: "10px",
                    position: "relative",
                  }}
                >
                  <div
                    style={{
                      position: "absolute",
                      left: "-8px",
                      top: "2px",
                      width: "12px",
                      height: "12px",
                      backgroundColor: "#52c41a",
                      borderRadius: "50%",
                    }}
                  />
                  <div style={{ fontWeight: "600", marginBottom: "4px" }}>
                    Order Confirmed
                  </div>
                  {/* <div style={{ fontSize: "12px", color: "#52c41a" }}>
                    {formatDate(currentTrackingOrder.orderdate)}
                  </div> */}
                </div>

                {/* Step 2: Packing Status */}
                <div
                  style={{
                    marginBottom: "20px",
                    paddingLeft: "10px",
                    position: "relative",
                  }}
                >
                  <div
                    style={{
                      position: "absolute",
                      left: "-8px",
                      top: "2px",
                      width: "12px",
                      height: "12px",
                      backgroundColor:
                        currentTrackingOrder.packing === 1
                          ? "#52c41a"
                          : "#d9d9d9",
                      borderRadius: "50%",
                    }}
                  />
                  <div style={{ fontWeight: "600", marginBottom: "4px" }}>
                    Packing
                  </div>
                  {currentTrackingOrder.packing === 1 &&
                    currentTrackingOrder.packingdate && (
                      <div style={{ fontSize: "12px", color: "#52c41a" }}></div>
                    )}
                </div>

                {/* Step 3: Dispatch Status */}
                <div
                  style={{
                    marginBottom: "20px",
                    paddingLeft: "10px",
                    position: "relative",
                  }}
                >
                  <div
                    style={{
                      position: "absolute",
                      left: "-8px",
                      top: "2px",
                      width: "12px",
                      height: "12px",
                      backgroundColor:
                        currentTrackingOrder.dispatch === 1
                          ? "#52c41a"
                          : "#d9d9d9",
                      borderRadius: "50%",
                    }}
                  />
                  <div style={{ fontWeight: "600", marginBottom: "4px" }}>
                    Dispatched
                  </div>
                  {currentTrackingOrder.dispatch === 1 &&
                    currentTrackingOrder.dispatchdate && (
                      <div style={{ fontSize: "12px", color: "#52c41a" }}>
                        Dispatched on:{" "}
                        {formatDate(currentTrackingOrder.dispatchdate)}
                        {currentTrackingOrder.transport &&
                          ` via ${currentTrackingOrder.transport}`}
                        {currentTrackingOrder.dispatchid &&
                          ` (AWB: ${currentTrackingOrder.dispatchid})`}
                      </div>
                    )}
                </div>

                {/* Step 4: Delivery Status */}
                <div
                  style={{
                    paddingLeft: "10px",
                    position: "relative",
                  }}
                >
                  <div
                    style={{
                      position: "absolute",
                      left: "-8px",
                      top: "2px",
                      width: "12px",
                      height: "12px",
                      backgroundColor: currentTrackingOrder.delivarydate
                        ? "#52c41a"
                        : "#d9d9d9",
                      borderRadius: "50%",
                    }}
                  />
                  <div style={{ fontWeight: "600", marginBottom: "4px" }}>
                    Delivered
                  </div>
                  {currentTrackingOrder.delivarydate && (
                    <div style={{ fontSize: "12px", color: "#52c41a" }}>
                      Delivered on:{" "}
                      {formatDate(currentTrackingOrder.delivarydate)}
                    </div>
                  )}
                </div>
              </div>

              {/* Additional tracking info if available */}
              {currentTrackingOrder.transport &&
                currentTrackingOrder.dispatchid && (
                  <div
                    style={{
                      marginTop: "20px",
                      padding: "12px",
                      backgroundColor: "#f5f5f5",
                      borderRadius: "6px",
                      fontSize: "13px",
                    }}
                  >
                    <strong style={{ display: "block", marginBottom: "5px" }}>
                      Tracking Information:
                    </strong>

                    <div>
                      Tracking Number: {currentTrackingOrder.dispatchid}
                    </div>
                  </div>
                )}
            </div>

            {/* Close Button */}
            <div
              style={{
                padding: "20px 30px",
                borderTop: "1px solid #f0f0f0",
                textAlign: "right",
              }}
            >
              <button
                onClick={closeTrackOrderModal}
                style={{
                  padding: "8px 25px",
                  backgroundColor: "#dc3545",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: "500",
                }}
              >
                Close
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* Cancel Order Modal */}
     <Modal
  title={
    <div
      style={{ fontSize: "18px", fontWeight: "600", color: "#1a1a1a" }}
    >
      Cancel Order Confirmation
    </div>
  }
  open={cancelModalVisible}
  onOk={handleCancelOrder}
  onCancel={() => {
    setCancelModalVisible(false);
    setCurrentOrderToCancel(null);
  }}
  okText="Confirm Cancellation"
  cancelText="Cancel"
okButtonProps={{
  disabled: !currentOrderToCancel?.cancellationdesc?.trim(),
  style: {
    backgroundColor: currentOrderToCancel?.cancellationdesc?.trim()
      ? "#dc3545"
      : "#d9d9d9",
    borderColor: currentOrderToCancel?.cancellationdesc?.trim()
      ? "#dc3545"
      : "#d9d9d9",
    color: "#fff",
  },
}}
  width={400}
  centered
>
  <div style={{ marginBottom: "20px" }}>
    <p style={{ marginBottom: "16px" }}>
      Are you sure you want to cancel this order? This action cannot be
      undone.
    </p>

    <div
      style={{
        backgroundColor: "#f9f9f9",
        padding: "16px",
        borderRadius: "6px",
        marginBottom: "16px",
      }}
    >
      <div style={{ marginBottom: "8px" }}>
        <strong>Order ID:</strong> {currentOrderToCancel?.orderid}
      </div>
      <div style={{ marginBottom: "8px" }}>
        <strong>Amount:</strong> ₹
        {Number(currentOrderToCancel?.netamount || 0).toFixed(2)}
      </div>
      <div style={{ marginBottom: "8px" }}>
        <strong>Date:</strong> {formatDate(currentOrderToCancel?.orderdate)}
      </div>

      <div>
        <label style={{ fontWeight: "500" }}>
          Reason for Cancellation <span style={{ color: "red" }}>*</span>
        </label>
        <textarea
          value={currentOrderToCancel?.cancellationdesc || ""}
          onChange={(e) =>
            setCurrentOrderToCancel({
              ...currentOrderToCancel,
              cancellationdesc: e.target.value, 
                 })
          }
          placeholder="Enter reason for cancelling this order..."
          rows={3}
          style={{
            width: "100%",
            marginTop: "6px",
            padding: "8px",
            borderRadius: "4px",
            border: "1px solid #ccc",
            resize: "none",
          }}
        />
      </div>
    </div>
  </div>
</Modal>
    </div>
  );
};

export default Orders;
